import { useState, useEffect, useRef } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Typography,
  Avatar,
  Dropdown,
  Space,
  Tag,
  message,
  Row,
  Col,
  Timeline,
  Divider,
  Spin,
  Empty,
  List,
  Progress,
  Checkbox
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  EyeOutlined,
  ArrowLeftOutlined,
  MessageOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  BulbOutlined,
  FileTextOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { getWsUrl } from '../../services/websocket';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

export default function SessionMonitor() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [monitoringSession, setMonitoringSession] = useState(null);
  const [evaluationCriteria, setEvaluationCriteria] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  useEffect(() => {
    if (sessionId) {
      fetchSessionDetails(sessionId);
      connectWebSocket(sessionId);
    } else {
      fetchActiveSessions();
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [sessionId]);

  const fetchActiveSessions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/facilitator/sessions/active');
      setSessions(response.data);
    } catch (error) {
      message.error('Failed to load active sessions');
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionDetails = async (id) => {
    try {
      setLoading(true);
      const response = await api.get(`/facilitator/sessions/${id}/monitor`);
      setMonitoringSession(response.data);

      // Fetch evaluation criteria for the lab
      if (response.data?.lab?.id) {
        try {
          const criteriaResponse = await api.get(`/evaluations/labs/${response.data.lab.id}/criteria`);
          setEvaluationCriteria(criteriaResponse.data || []);
        } catch (error) {
          console.error('Error fetching evaluation criteria:', error);
          setEvaluationCriteria([]);
        }
      }
    } catch (error) {
      message.error('Failed to load session details');
      console.error('Error fetching session details:', error);
      navigate('/facilitator/sessions');
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = (id) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsUrl = `${getWsUrl()}/ws/facilitator/${id}?token=${token}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('Facilitator WebSocket connected for session:', id);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'new_message') {
          // Add new message to timeline
          setMonitoringSession(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              messages: [...(prev.messages || []), {
                id: data.message.id,
                sender_type: data.message.sender_type,
                sender_name: data.message.sender_name,
                content: data.message.content,
                sent_at: data.message.sent_at || new Date().toISOString()
              }]
            };
          });
        } else if (data.type === 'shared_knowledge_update') {
          // Add new shared knowledge entry
          setMonitoringSession(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              shared_knowledge: [...(prev.shared_knowledge || []), {
                id: data.knowledge.id,
                content: data.knowledge.content,
                reason: data.knowledge.reason,
                created_at: data.knowledge.created_at || new Date().toISOString()
              }]
            };
          });
        } else if (data.type === 'session_status_update') {
          // Update session status
          setMonitoringSession(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              session: {
                ...prev.session,
                status: data.status,
                completed_at: data.completed_at,
                duration_seconds: data.duration_seconds
              }
            };
          });
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('Facilitator WebSocket disconnected');
      // Attempt to reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        if (sessionId) {
          connectWebSocket(sessionId);
        }
      }, 3000);
    };

    wsRef.current = ws;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/facilitator/dashboard'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const sessionColumns = [
    {
      title: 'Student',
      key: 'student',
      render: (_, record) => (
        <Space>
          <Avatar style={{ backgroundColor: '#faad14' }} icon={<UserOutlined />} />
          <div>
            <div><Text strong>{`${record.user.first_name} ${record.user.last_name}`}</Text></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.user.email}</Text></div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Lab',
      dataIndex: ['lab', 'name'],
      key: 'lab',
    },
    {
      title: 'Training',
      dataIndex: ['training', 'name'],
      key: 'training',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          active: 'green',
          evaluating: 'orange',
          completed: 'blue'
        };
        return <Tag color={colors[status]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_, record) => {
        const startTime = new Date(record.started_at);
        const endTime = record.completed_at ? new Date(record.completed_at) : new Date();
        const durationMs = endTime - startTime;
        const minutes = Math.floor(durationMs / 60000);
        return (
          <Space>
            <ClockCircleOutlined />
            <Text>{minutes} min</Text>
          </Space>
        );
      },
    },
    {
      title: 'Messages',
      dataIndex: 'message_count',
      key: 'message_count',
      render: (count) => `${count} messages`,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/facilitator/sessions/${record.id}`)}
        >
          Monitor
        </Button>
      ),
    },
  ];

  const renderSessionsList = () => (
    <>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Active Lab Sessions</Title>
        <Text type="secondary">Monitor student lab sessions in real-time</Text>
      </div>

      <Card>
        <Table
          columns={sessionColumns}
          dataSource={sessions}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </>
  );

  const renderSessionMonitor = () => {
    if (!monitoringSession) {
      return <Spin size="large" />;
    }

    const { session, user: student, lab, messages, shared_knowledge } = monitoringSession;

    return (
      <>
        <div style={{ marginBottom: 24 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/facilitator/sessions')}
            style={{ marginBottom: 16 }}
          >
            Back to Sessions
          </Button>
          <Title level={2}>Session Monitor</Title>
          <Text type="secondary">Real-time view of student lab session</Text>
        </div>

        {/* Session Info */}
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Space direction="vertical" size="small">
                <Text type="secondary">Student</Text>
                <Space>
                  <Avatar style={{ backgroundColor: '#faad14' }} icon={<UserOutlined />} />
                  <div>
                    <div><Text strong>{`${student.first_name} ${student.last_name}`}</Text></div>
                    <div><Text type="secondary" style={{ fontSize: 12 }}>{student.email}</Text></div>
                  </div>
                </Space>
              </Space>
            </Col>
            <Col xs={24} md={8}>
              <Space direction="vertical" size="small">
                <Text type="secondary">Lab</Text>
                <Text strong>{lab.name}</Text>
              </Space>
            </Col>
            <Col xs={24} md={8}>
              <Space direction="vertical" size="small">
                <Text type="secondary">Status</Text>
                <Tag color={session.status === 'active' ? 'green' : 'orange'}>
                  {session.status.toUpperCase()}
                </Tag>
              </Space>
            </Col>
          </Row>
          <Divider />
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Space direction="vertical" size="small">
                <Text type="secondary">Started</Text>
                <Text>{new Date(session.started_at).toLocaleString()}</Text>
              </Space>
            </Col>
            {session.completed_at && (
              <Col xs={24} md={8}>
                <Space direction="vertical" size="small">
                  <Text type="secondary">Completed</Text>
                  <Text>{new Date(session.completed_at).toLocaleString()}</Text>
                </Space>
              </Col>
            )}
            <Col xs={24} md={8}>
              <Space direction="vertical" size="small">
                <Text type="secondary">Duration</Text>
                <Text>{Math.floor((session.duration_seconds || 0) / 60)} minutes</Text>
              </Space>
            </Col>
          </Row>
        </Card>

        <Row gutter={[16, 16]}>
          {/* Message Timeline */}
          <Col xs={24} lg={12}>
            <Card
              title={
                <Space>
                  <MessageOutlined style={{ color: '#faad14' }} />
                  <Text strong>Message Timeline</Text>
                </Space>
              }
              extra={
                <Space>
                  <Tag color="blue">{messages.length} messages</Tag>
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={() => fetchSessionDetails(sessionId)}
                  />
                </Space>
              }
              style={{ height: '600px', overflowY: 'auto' }}
            >
              {messages.length === 0 ? (
                <Empty description="No messages yet" />
              ) : (
                <Timeline>
                  {messages.map((msg) => (
                    <Timeline.Item
                      key={msg.id}
                      color={msg.sender_type === 'student' ? 'blue' : '#faad14'}
                      dot={
                        msg.sender_type === 'student' ? (
                          <UserOutlined />
                        ) : (
                          <MessageOutlined />
                        )
                      }
                    >
                      <div style={{ marginBottom: 8 }}>
                        <Space>
                          <Text strong>{msg.sender_name}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {new Date(msg.sent_at).toLocaleTimeString()}
                          </Text>
                          <Tag color={msg.sender_type === 'student' ? 'blue' : 'orange'}>
                            {msg.sender_type}
                          </Tag>
                        </Space>
                      </div>
                      <Card size="small" style={{ backgroundColor: '#f9f9f9' }}>
                        <Text>{msg.content}</Text>
                      </Card>
                    </Timeline.Item>
                  ))}
                </Timeline>
              )}
            </Card>
          </Col>

          {/* Right Column: Shared Knowledge + Evaluation Criteria */}
          <Col xs={24} lg={12}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {/* Shared Knowledge */}
              <Card
                title={
                  <Space>
                    <BulbOutlined style={{ color: '#faad14' }} />
                    <Text strong>Shared Knowledge (AI Coordination)</Text>
                  </Space>
                }
                extra={<Tag color="orange">{shared_knowledge.length} entries</Tag>}
                style={{ height: '280px', overflowY: 'auto' }}
              >
                {shared_knowledge.length === 0 ? (
                  <Empty description="No shared knowledge yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    {shared_knowledge.map((knowledge) => (
                      <Card
                        key={knowledge.id}
                        size="small"
                        style={{ backgroundColor: '#fffbe6' }}
                      >
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <Text strong style={{ color: '#faad14', fontSize: 12 }}>
                            {knowledge.reason || 'Knowledge Shared'}
                          </Text>
                          <Text style={{ fontSize: 13 }}>{knowledge.content}</Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {new Date(knowledge.created_at).toLocaleString()}
                          </Text>
                        </Space>
                      </Card>
                    ))}
                  </Space>
                )}
              </Card>

              {/* Evaluation Criteria Checklist */}
              <Card
                title={
                  <Space>
                    <FileTextOutlined style={{ color: '#1890ff' }} />
                    <Text strong>Evaluation Criteria</Text>
                  </Space>
                }
                extra={
                  evaluationCriteria.length > 0 ? (
                    <Tag color="blue">{evaluationCriteria.length} criteria</Tag>
                  ) : null
                }
                style={{ height: '300px', overflowY: 'auto' }}
              >
                {evaluationCriteria.length === 0 ? (
                  <Empty description="No evaluation criteria configured" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  <List
                    dataSource={evaluationCriteria}
                    renderItem={(criterion) => (
                      <List.Item style={{ padding: '8px 0' }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                            <Text strong>{criterion.criterion_name}</Text>
                            {criterion.weight && (
                              <Tag color="geekblue">Weight: {criterion.weight}%</Tag>
                            )}
                          </Space>
                          {criterion.description && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {criterion.description}
                            </Text>
                          )}
                          {criterion.evaluation_prompt && (
                            <Card size="small" style={{ backgroundColor: '#f0f5ff', marginTop: 4 }}>
                              <Text style={{ fontSize: 11, fontStyle: 'italic' }}>
                                AI Prompt: {criterion.evaluation_prompt.substring(0, 100)}
                                {criterion.evaluation_prompt.length > 100 ? '...' : ''}
                              </Text>
                            </Card>
                          )}
                        </Space>
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            </Space>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }} onClick={() => navigate('/facilitator/dashboard')}>
            USSEDU Platform
          </Title>
          <Text type="secondary">Session Monitor</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        {sessionId ? renderSessionMonitor() : renderSessionsList()}
      </Content>
    </Layout>
  );
}
